import { MailIcon } from "lucide-react";
import { useCallback } from "react";

import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from "@/components/ui/combobox";
import { cn } from "@/lib/utils";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { MailAccount } from "./types";

interface AccountSwitcherProps {
	isCollapsed: boolean;
	accounts: MailAccount[];
	selectedConnectionId: Id<"oauthConnections">;
	onSelect: (connectionId: Id<"oauthConnections">) => void;
}

function providerLabel(provider: "gmail" | "ms-outlook"): string {
	return provider === "gmail" ? "Gmail" : "Outlook";
}

function ProviderIcon({ provider }: { provider: "gmail" | "ms-outlook" }) {
	return (
		<MailIcon
			className={cn(
				"h-4 w-4 shrink-0",
				provider === "gmail" ? "text-destructive" : "text-primary",
			)}
		/>
	);
}

export function AccountSwitcher({
	isCollapsed,
	accounts,
	selectedConnectionId,
	onSelect,
}: AccountSwitcherProps) {
	const selected =
		accounts.find((a) => a.connectionId === selectedConnectionId) ??
		accounts[0];

	const resetScrollOnMount = useCallback((node: HTMLInputElement | null) => {
		if (!node) return;
		node.scrollLeft = 0;
		node.setSelectionRange(0, 0);
	}, []);

	return (
		<Combobox
			items={accounts}
			value={selected}
			onValueChange={(next: MailAccount | null) => {
				if (next) onSelect(next.connectionId);
			}}
			itemToStringLabel={(acc: MailAccount) =>
				acc.accountEmail || providerLabel(acc.provider)
			}
			itemToStringValue={(acc: MailAccount) => acc.connectionId}
		>
			<ComboboxInput
				ref={resetScrollOnMount}
				placeholder="Select account"
				className={cn(
					"w-full [&_input]:text-ellipsis",
					isCollapsed && "hidden",
				)}
				onFocus={(e) => {
					e.currentTarget.select();
				}}
				onBlur={(e) => {
					e.currentTarget.scrollLeft = 0;
					e.currentTarget.setSelectionRange(0, 0);
				}}
			/>
			<ComboboxContent>
				<ComboboxEmpty>No mailboxes found.</ComboboxEmpty>
				<ComboboxList>
					{(acc: MailAccount) => (
						<ComboboxItem key={acc.connectionId} value={acc}>
							<div className="flex min-w-0 flex-1 items-center gap-2">
								<ProviderIcon provider={acc.provider} />
								<div className="flex min-w-0 flex-col">
									<span className="truncate text-sm">{acc.accountEmail}</span>
									<span className="truncate text-xs text-muted-foreground">
										{acc.ownerName} · {providerLabel(acc.provider)}
									</span>
								</div>
							</div>
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
}
